import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Cookies from "js-cookie";

export const ViewColleagueModal = ({
    experience,
    onClose,
    onSuccess,
    type, // "work" or "education"
}) => {
    const [contacts, setContacts] = useState([]);
    const [friendsList, setFriendsList] = useState([]); // from get_contact_add_*
    const [selected, setSelected] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true); // For initial API loading
    const token = Cookies.get("auth_token");

    const idKey = type === "work" ? "experience_id" : "education_id";
    const idValue = type === "work"
        ? experience.experience_id
        : experience.education_id;

    const getFriendsEndpoint =
        type === "work"
            ? "/get_contact_add_work_experience"
            : "/get_contact_add_education";

    const setFriendsEndpoint =
        type === "work"
            ? "/set_contact_add_work_experience"
            : "/set_contact_add_education";
    const setUpdateMessage =
        type === "work"
            ? "Work experience colleague list updated successfully!"
            : "Education classmates list updated successfully!";

    const isSelectableColleagueContact = (contact) => {
        if (Number(contact?.phone_book_user) !== 1) return false;
        if (String(contact?.type || "").toLowerCase() === "broadcast_msg") return false;
        return true;
    };

    useEffect(() => {
        fetchContactsAndFriends();
    }, []);

    const fetchContactsAndFriends = async () => {
        setFetching(true);
        try {
            // 1️⃣ Fetch all contacts
            const contactRes = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/get-contacts`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const contactData = await contactRes.json();
            if (contactData.status !== "1") {
                toast.dismiss();
                toast.error(contactData.message || "Failed to load contacts");
                setFetching(false);
                return;
            }

            const rawContacts = (contactData.data[0]?.contacts || [])
                .filter(isSelectableColleagueContact);

            // ⭐ Normalize: always use .phone inside this component
            const allContacts = rawContacts.map((c) => ({
                ...c,
                phone: c.phone || c.phone_number, // <- key point
            }));

            // 2️⃣ Fetch already added friends
            const payload = new URLSearchParams({ [idKey]: idValue });
            const friendRes = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}${getFriendsEndpoint}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        Authorization: `Bearer ${token}`,
                    },
                    body: payload.toString(),
                }
            );
            const friendData = await friendRes.json();

            if (friendData.status !== "1") {
                toast.dismiss();
                toast.error(friendData.message || "Failed to load existing friends");
                setContacts(allContacts);
                setFetching(false);
                return;
            }

            const addedFriends = friendData?.contacts || [];

            // 3️⃣ Pre-select contacts that are already friends
            const preSelected = allContacts.filter((contact) =>
                addedFriends.some(
                    (f) => (f.phone_number || f.phone) === contact.phone
                )
            );
            setContacts(allContacts);
            setFriendsList(addedFriends);
            setSelected(preSelected);
        } catch (err) {
            toast.dismiss();
            toast.error(err.message);
        } finally {
            setFetching(false);
        }
    };


    // ✅ Toggle selection
    const toggleSelect = (person) => {
        setSelected((prev) => {
            const exists = prev.find((p) => p.phone === person.phone);
            return exists
                ? prev.filter((p) => p.phone !== person.phone)
                : [...prev, person];
        });
    };



    // ✅ Confirm and send updates
    const handleConfirm = async () => {
        if (contacts.length === 0) return;

        setLoading(true);
        try {
            const contactsPayload = contacts.map((person) => {
                const wasFriend = friendsList.some(
                    (f) => (f.phone || f.phone_number) === person.phone
                );
                const isNowFriend = selected.some((s) => s.phone === person.phone);

                let action = "Remove";
                if (isNowFriend && !wasFriend) action = "Friend";
                else if (isNowFriend && wasFriend) action = "Friend";
                else if (!isNowFriend && wasFriend) action = "Remove";

                return {
                    name: person.full_name,
                    phone_number: person.phone,
                    country_code: person.country_code,
                    action,
                };
            });


            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}${setFriendsEndpoint}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        [idKey]: idValue,
                        contacts: contactsPayload,
                    }),
                }
            );

            const data = await res.json();

            if (data.status === "1") {
                // ✅ Check for invalid contacts in response
                const invalidContacts =
                    data?.data?.[0]?.invalid_contacts || [];

                if (invalidContacts.length > 0) {
                    // Build a user-friendly message
                    const invalidList = invalidContacts
                        .map(
                            (c) =>
                                `${c.phone} (${c.reason || "Invalid contact"})`
                        )
                        .join(", ");
                    toast.dismiss();
                    toast.error(
                        `Some contacts could not be added: ${invalidList}`,
                        { duration: 7000 }
                    );
                } else {
                    toast.success(setUpdateMessage || "Colleague list updated successfully!");
                    onSuccess();
                }

                setTimeout(() => onClose(), 800);
            } else {
                toast.dismiss();
                toast.error(data.message || "Failed to update colleagues");
            }
        } catch (err) {
            toast.dismiss();
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredContacts = contacts.filter((c) =>
        c.full_name?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[999]">
            <div className="bg-white rounded-3xl shadow-2xl w-[420px]  p-6 pb-3 relative
             [&::-webkit-scrollbar]:w-1
             [&::-webkit-scrollbar-track]:my-4
            [&::-webkit-scrollbar-thumb]:bg-gray-400 
            [&::-webkit-scrollbar-thumb]:rounded-full 
            [&::-webkit-scrollbar-track]:bg-gray-100">
                {/* Sticky Header + Search */}
                <div className="sticky top-0 bg-white pb-3 z-20">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Add Colleague
                        </h2>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={onClose}
                                aria-label="Close"
                                className="p-2 rounded-full hover:bg-gray-100"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 8.586L15.95 2.636a1 1 0 111.414 1.414L11.414 10l5.95 5.95a1 1 0 01-1.414 1.414L10 11.414l-5.95 5.95A1 1 0 012.636 15.95L8.586 10 2.636 4.05A1 1 0 014.05 2.636L10 8.586z" clipRule="evenodd" />
                                </svg>
                            </button>

                            {/* <button
                                onClick={handleConfirm}
                                disabled={loading}
                                className={`rounded-full shadow-md transition ${loading ? "bg-gray-400 cursor-not-allowed" : ""
                                    }`}
                            >
                                <img
                                    src="../chatbar/right.svg"
                                    alt="Confirm"
                                    className="w-7 h-7 rounded-lg"
                                />
                            </button> */}
                        </div>
                    </div>

                    {/* Search Input */}
                    <div className="relative w-full mb-3">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                            <img
                                src="../chatbox/magnifer.svg"
                                alt="Search"
                                className="w-5 h-5"
                            />
                        </div>
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search"
                        className="w-full rounded-[20px] border border-gray-400 bg-white pl-12 pr-12 py-3 text-gray-800 placeholder:text-gray-400 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15 shadow-md shadow-[#7C4DFF] sticky top-4 [appearance:textfield] [&::-webkit-search-cancel-button]:hidden"

                        />
                        {search && (
                            <button
                                type="button"
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={() => setSearch("")}
                            >
                                <img
                                    src="../chatbox/close.svg"
                                    alt="Clear"
                                    className="w-5 h-5"
                                />
                            </button>
                        )}
                    </div>
                </div>

                {/* Contact List (fixed height, scrollable) */}
                <div className="space-y-3 max-h-[40vh] overflow-y-auto py-2">
                    {fetching ? (
                        // Loading Skeleton
                        <>
                            {Array.from({ length: 5 }).map((_, idx) => (
                                <div
                                    key={idx}
                                    className="flex items-center justify-between p-3 rounded-[100px] bg-gray-200 animate-pulse"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-gray-300" />
                                        <div className="flex-1">
                                            <div className="h-4 w-24 bg-gray-300 rounded mb-2" />
                                            <div className="h-3 w-20 bg-gray-300 rounded" />
                                        </div>
                                    </div>
                                    <div className="h-6 w-6 rounded-full bg-gray-300" />
                                </div>
                            ))}
                        </>
                    ) : filteredContacts.length === 0 ? (
                        <div className="text-center py-10 text-gray-500">
                            {contacts.length === 0 ? (
                                <>
                                    <p className="font-semibold">No contacts available</p>
                                    <p className="text-xs">You don't have any contacts yet.</p>
                                </>
                            ) : (
                                <>
                                    <p className="font-semibold">No results</p>
                                    <p className="text-xs">No contacts match "{search}".</p>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            {filteredContacts.map((person, index) => {
                                const isSelected = selected.some((p) => p.phone === person.phone);

                                return (
                                    <div
                                        key={person.phone}
                                        onClick={() => toggleSelect(person)}
                                        className={`flex items-center justify-between p-3 rounded-[100px] border border-gray-100 shadow-md cursor-pointer transition-all duration-200 hover:shadow-lg  ${isSelected
                                            ? "bg-[#EDE7FF] border-[#7C4DFF]"
                                            : "bg-[#797C7B] bg-opacity-[11%]"
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <img
                                                src={
                                                    person.profile_image
                                                        ? `${process.env.REACT_APP_API_BASE_URL}${person.profile_image}`
                                                        : "/default-avatar.png"
                                                }
                                                alt={person?.full_name}
                                                className="h-10 w-10 rounded-full object-cover"
                                            />
                                            <div>
                                                <p className="font-semibold text-sm text-gray-900">
                                                    {person?.full_name}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {person?.phone}
                                                </p>
                                            </div>
                                        </div>

                                        <div
                                            className={`w-6 h-6 flex items-center justify-center rounded-full border-2 transition-all duration-200 ${isSelected
                                                ? "bg-[#7C4DFF] border-[#7C4DFF]"
                                                : "border-gray-400"
                                                }`}
                                        >
                                            {isSelected && (
                                                <img
                                                    src="../chatscreen/radiobutton.svg"
                                                    alt="Selected"
                                                    className="w-6 h-6 text-white"
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                        </>
                    )}
                </div>

                <div className="flex justify-center">
                    <button onClick={handleConfirm}
                        disabled={loading || fetching}
                        className={`rounded-full shadow-md mt-3 transition submit-button disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${loading || fetching ? "bg-gray-400" : ""
                            }`}>
                        {loading ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Adding...
                            </>
                        ) : (
                            'Add'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
